import React from "react";
import { Chip, FormControl, FormLabel, Option, Stack } from "@mui/joy";
import CommonModal from "../../common/CommonModal";
import DarkSelect from "../../common/DarkSelect";
import DarkTypography from "../../common/DarkTypography";
import { useBilling } from "../../../contexts/api/billing";
import { textTokens } from "../../../styles/globalStyles";
import type { Product } from "../../../api/billing/Billing.typed";
import { DEFAULT_TAX_RATE, formatPrice, htToTtc } from "../../billing/shared/billingUtils";
import { useTraining } from "../../../contexts/api";

type Props = {
  open: boolean;
  onClose: () => void;
  customerId: number;
  onPurchased: () => void;
};

export default function PurchaseProductModal({ open, onClose, customerId, onPurchased }: Props) {
  const { listProducts, purchaseProduct } = useBilling();
  const { qualifications, refreshTrainingTemplates } = useTraining();
  const [products, setProducts] = React.useState<Product[]>([]);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    void listProducts().then((data) => {
      if (data) setProducts(data.filter((p) => p.IsActive));
    });
    setSelectedId(null);
  }, [open, listProducts]);

  React.useEffect(() => {
    if (open && !qualifications) void refreshTrainingTemplates();
  }, [open, qualifications, refreshTrainingTemplates]);

  const selectedProduct = products.find((p) => p.ID === selectedId);
  const qualifMap = React.useMemo(() => {
    const m = new Map<number, string>();
    qualifications?.forEach((q) => m.set(q.ID, `${q.Agency} – ${q.Qualification}`));
    return m;
  }, [qualifications]);

  const selectedTrainingTemplateIds = React.useMemo<number[]>(() => {
    if (!selectedProduct) return [];
    const seen = new Set<number>();
    for (const line of selectedProduct.Lines ?? []) {
      const ids = line.prestation?.TrainingTemplateIDs ?? [];
      for (const id of ids) seen.add(id);
    }
    return [...seen];
  }, [selectedProduct]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    const result = await purchaseProduct({ customer_id: customerId, product_id: selectedId });
    setSubmitting(false);
    if (result === undefined) return;
    onPurchased();
    onClose();
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Acheter un produit"
      size="xl"
      fitContentHeight
      onConfirm={handleConfirm}
      confirmLabel="Acheter"
      submitDisabled={!selectedId}
      loading={submitting}
    >
      <Stack spacing={2}>
        <FormControl required>
          <FormLabel sx={{ color: textTokens.color.dimmed }}>Produit</FormLabel>
          <DarkSelect
            placeholder="Choisir un produit..."
            value={selectedId}
            onChange={(_, v) => setSelectedId(v as number | null)}
          >
            {products.map((p) => (
              <Option key={p.ID} value={p.ID}>
                {p.Name} —{" "}
                {formatPrice(htToTtc(p.Price, p.TaxRate ?? DEFAULT_TAX_RATE), p.Currency || "EUR")}{" "}
                TTC
                {p.IsUnlimited ? " (Illimité)" : ""}
              </Option>
            ))}
          </DarkSelect>
        </FormControl>
        {selectedTrainingTemplateIds.length > 0 && (
          <Stack spacing={0.5}>
            <DarkTypography level="body-sm" sx={{ color: textTokens.color.dimmed }}>
              Formations créées automatiquement à l'achat :
            </DarkTypography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {selectedTrainingTemplateIds.map((id) => (
                <Chip key={id} size="sm" variant="soft" color="primary">
                  {qualifMap.get(id) ?? `Formation #${id}`}
                </Chip>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </CommonModal>
  );
}
